import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Lock, Sparkles, AlertTriangle, Copy, Check, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAffiliateStatus } from "@/hooks/useAffiliateStatus";
import { useUsername } from "@/hooks/useUsername";
import { useUserRole } from "@/hooks/useUserRole";
import { toast } from "sonner";

/**
 * Affiliate program card.
 *
 * Renders one of four states based on `useAffiliateStatus`:
 *   1. Pre-payment — locked, "Available after your first subscription payment"
 *   2. Eligible    — show enrollment CTA
 *   3. Enrolled & active — show referral link + leave button
 *   4. Lapsed     — already enrolled, sub now inactive
 *
 * Mounting is gated by `tierAffiliateAvailable` in Settings.tsx — this card
 * only ever renders for tiers where the program is offered.
 */
export function AffiliateCard() {
  const { username } = useUsername();
  const { status, loading, refresh } = useAffiliateStatus();
  const { isSuperAdmin } = useUserRole();
  const [enrolling, setEnrolling] = useState(false);
  const [leaving, setLeaving] = useState(false);

  // Super admins always behave like Super Affiliates: pre-enrollment they
  // see the eligible CTA; post-enrollment they see the enrolled state with
  // the Super Affiliate badge (driven by the enrollment row's flag).
  const treatAsSuper = !!status?.is_super_affiliate || isSuperAdmin;

  const enroll = async () => {
    setEnrolling(true);
    const args: { _profile_id?: string } = {};
    if (status?.profile_id) args._profile_id = status.profile_id;
    const { error } = await supabase.rpc("enroll_in_affiliate_program", args);
    setEnrolling(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Enrolled in the affiliate program");
    await refresh();
  };

  const leave = async () => {
    setLeaving(true);
    const args: { _profile_id?: string } = {};
    if (status?.profile_id) args._profile_id = status.profile_id;
    const { error } = await supabase.rpc("leave_affiliate_program", args);
    setLeaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Affiliate enrollment paused");
    await refresh();
  };

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <CardTitle>Affiliate program</CardTitle>
            {treatAsSuper ? (
              <span className="inline-flex items-center rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                Super Affiliate
              </span>
            ) : null}
          </div>
          {status?.is_enrolled ? (
            <Badge variant={status.eligible_now ? "default" : "secondary"}>
              {status.eligible_now ? "Earning" : "Paused"}
            </Badge>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-muted-foreground">
          {treatAsSuper
            ? "Test/promotional account — commissions accrue normally but are tracked separately and excluded from program KPIs."
            : "Refer Excerptus to your audience and earn commission on every paid subscription that comes from your link. Available to anyone with an active paid subscription."}
        </p>

        {loading ? (
          <p className="text-xs text-muted-foreground">Checking your eligibility…</p>
        ) : !status ? (
          <p className="text-xs text-muted-foreground">Sign in to view affiliate status.</p>
        ) : treatAsSuper ? (
          status.is_enrolled ? (
            <EnrolledActiveState username={username} onLeave={leave} leaving={leaving} />
          ) : (
            <EligibleState onEnroll={enroll} enrolling={enrolling} />
          )
        ) : !status.has_ever_paid && !status.override_eligible ? (
          <PrePaymentState />
        ) : status.is_enrolled ? (
          status.eligible_now ? (
            <EnrolledActiveState username={username} onLeave={leave} leaving={leaving} />
          ) : (
            <LapsedState />
          )
        ) : (
          <EligibleState onEnroll={enroll} enrolling={enrolling} />
        )}
      </CardContent>
    </Card>
  );
}

function PrePaymentState() {
  return (
    <div className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3">
      <Lock className="mt-0.5 h-4 w-4 text-muted-foreground" />
      <div className="space-y-1">
        <p className="font-medium">Locked</p>
        <p className="text-xs text-muted-foreground">
          Available after your first successful subscription payment.
        </p>
      </div>
    </div>
  );
}

function EligibleState({ onEnroll, enrolling }: { onEnroll: () => void; enrolling: boolean }) {
  return (
    <div className="space-y-3 rounded-md border border-primary/30 bg-primary/5 p-3">
      <div className="flex items-start gap-2">
        <Sparkles className="mt-0.5 h-4 w-4 text-primary" />
        <div className="space-y-1">
          <p className="font-medium">You're eligible</p>
          <p className="text-xs text-muted-foreground">
            Enroll to get your referral link. You'll keep earning as long as your subscription stays active.
          </p>
        </div>
      </div>
      <Button size="sm" onClick={onEnroll} disabled={enrolling}>
        {enrolling ? "Enrolling…" : "Enroll in affiliate program"}
      </Button>
    </div>
  );
}

function EnrolledActiveState({
  username,
  onLeave,
  leaving,
}: {
  username: string | null;
  onLeave: () => void;
  leaving: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const referralLink = username
    ? `${typeof window !== "undefined" ? window.location.origin : ""}/?ref=${username}`
    : "";

  const copy = async () => {
    if (!referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success("Referral link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy. Select and copy manually.");
    }
  };

  return (
    <div className="space-y-3 rounded-md border border-primary/30 bg-primary/5 p-3">
      <div className="flex items-start gap-2">
        <Sparkles className="mt-0.5 h-4 w-4 text-primary" />
        <div className="space-y-1">
          <p className="font-medium">Enrolled — actively earning</p>
          <p className="text-xs text-muted-foreground">
            Share your referral link below. Payout setup will appear here once that module ships.
          </p>
        </div>
      </div>

      {referralLink ? (
        <div className="flex items-center gap-2">
          <code className="flex-1 truncate rounded bg-background px-2 py-1.5 text-xs font-mono text-foreground">
            {referralLink}
          </code>
          <Button size="sm" variant="outline" onClick={copy} aria-label="Copy referral link">
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          </Button>
        </div>
      ) : null}

      <div className="flex items-center justify-between gap-2">
        <Button asChild size="sm" variant="default">
          <Link to="/affiliate">
            View dashboard <ExternalLink className="ml-1 h-3.5 w-3.5" />
          </Link>
        </Button>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" variant="ghost" className="text-muted-foreground" disabled={leaving}>
              {leaving ? "Leaving…" : "Leave program"}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Leave the affiliate program?</AlertDialogTitle>
              <AlertDialogDescription>
                Your enrollment will be paused. You can re-enroll any time as long as your subscription stays active.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={onLeave}>Leave program</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

function LapsedState() {
  return (
    <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3">
      <AlertTriangle className="mt-0.5 h-4 w-4 text-destructive" />
      <div className="space-y-1">
        <p className="font-medium">Earnings paused</p>
        <p className="text-xs text-muted-foreground">
          Your enrollment is retained, but commissions only accrue while your paid subscription is active.
          Reactivate your subscription to resume earning.
        </p>
      </div>
    </div>
  );
}
